import { getMimeTypeFromFilename } from './fileUtils';

/** Legacy/binary Office; DOCX uses HTML conversion on the server */
export const OFFICE_EXT = new Set(['doc', 'xls', 'xlsx', 'ppt', 'pptx']);
export const TEXT_LIKE_EXT = new Set([
  'js', 'jsx', 'ts', 'tsx', 'css', 'html', 'json', 'md', 'txt', 'xml', 'csv', 'yaml', 'yml',
]);

export type PreviewCategory =
  | 'image'
  | 'pdf'
  | 'video'
  | 'audio'
  | 'text'
  | 'docx'
  | 'office'
  | 'unsupported';

export interface PreviewFileLike {
  id?: string;
  name: string;
  mimetype?: string;
  mimeType?: string;
  metadata?: { fileFormat?: string; [key: string]: unknown };
}

export function extensionOf(name: string): string {
  return name.split('.').pop()?.toLowerCase() || '';
}

export function getPreviewMimeType(file: PreviewFileLike): string {
  return (
    file.metadata?.fileFormat ||
    file.mimetype ||
    file.mimeType ||
    getMimeTypeFromFilename(file.name) ||
    ''
  );
}

export function getPreviewCategory(mime: string, ext: string): PreviewCategory {
  if (!mime && !ext) return 'unsupported';

  const isDocx =
    ext === 'docx' ||
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

  if (OFFICE_EXT.has(ext) && !isDocx) return 'office';
  if (isDocx) return 'docx';
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('text/') || TEXT_LIKE_EXT.has(ext)) return 'text';
  return 'unsupported';
}

export function canPreviewInBrowser(file: PreviewFileLike): boolean {
  const mime = getPreviewMimeType(file);
  const ext = extensionOf(file.name);
  const category = getPreviewCategory(mime, ext);
  return category !== 'unsupported' && category !== 'office';
}

export function needsTokenizedPreviewUrl(mime: string): boolean {
  return Boolean(
    mime &&
      (mime.startsWith('image/') ||
        mime.startsWith('video/') ||
        mime.startsWith('audio/') ||
        mime === 'application/pdf')
  );
}

export function isTransparentImageMime(mime: string): boolean {
  return (
    mime === 'image/png' ||
    mime === 'image/webp' ||
    mime === 'image/svg+xml' ||
    mime === 'image/gif'
  );
}

/**
 * User-facing file type label — never dump raw MIME strings in primary UI.
 */
export function getFriendlyFileType(fileName: string, mimeType?: string | null): string {
  const ext = extensionOf(fileName);
  const mime = (mimeType || '').toLowerCase();

  if (
    ext === 'xlsx' ||
    ext === 'xls' ||
    mime.includes('spreadsheetml') ||
    mime === 'application/vnd.ms-excel'
  ) {
    return 'Excel spreadsheet';
  }
  if (ext === 'csv' || mime === 'text/csv' || mime === 'application/csv') {
    return 'CSV spreadsheet';
  }
  if (
    ext === 'docx' ||
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return 'Word document';
  }
  if (ext === 'doc' || mime === 'application/msword') {
    return 'Word document';
  }
  if (
    ext === 'pptx' ||
    ext === 'ppt' ||
    mime.includes('presentation')
  ) {
    return 'Presentation';
  }
  if (mime === 'application/pdf' || ext === 'pdf') return 'PDF';
  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
    return 'Image';
  }
  if (mime.startsWith('video/')) return 'Video';
  if (mime.startsWith('audio/')) return 'Audio';
  if (mime.startsWith('text/') || TEXT_LIKE_EXT.has(ext)) return 'Text';
  if (mime.includes('zip') || mime.includes('compressed') || ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
    return 'Archive';
  }
  if (ext) return `${ext.toUpperCase()} file`;
  if (mime) {
    const top = mime.split('/')[0];
    return top.charAt(0).toUpperCase() + top.slice(1);
  }
  return 'File';
}

/**
 * Which first-party CYNAYD app opens this file, if any.
 */
export function getOpenedWithApp(fileName: string, mimeType?: string | null): 'CYNAYD Grid' | 'CYNAYD Docs' | null {
  const ext = extensionOf(fileName);
  const mime = (mimeType || '').toLowerCase();
  if (
    ext === 'xlsx' ||
    ext === 'csv' ||
    mime.includes('spreadsheetml') ||
    mime === 'text/csv' ||
    mime === 'application/csv'
  ) {
    return 'CYNAYD Grid';
  }
  if (
    ext === 'docx' ||
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return 'CYNAYD Docs';
  }
  return null;
}

export function syntaxLanguageFromExtension(ext: string): string | null {
  const map: Record<string, string> = {
    js: 'javascript',
    jsx: 'jsx',
    ts: 'typescript',
    tsx: 'tsx',
    json: 'json',
    css: 'css',
    html: 'html',
    xml: 'xml',
    md: 'markdown',
    txt: 'text',
    csv: 'csv',
    yaml: 'yaml',
    yml: 'yaml',
  };
  return map[ext] ?? null;
}
